// Cuts away the empty border around a logo (see-through, or one plain colour), so the mark itself fills its space
// in the LINE menu and the booking screens' header. Many logo files are a small mark in a big empty picture.

export interface Box { x: number; y: number; w: number; h: number }

/**
 * The part of an RGBA picture that holds the logo, with a little breathing room, or null when there is nothing worth
 * cutting (no plain border, or the border is already thin).
 */
export function contentBox(data: Uint8ClampedArray, w: number, h: number): Box | null {
  if (w < 2 || h < 2) return null;
  const at = (x: number, y: number) => (y * w + x) * 4;
  const corners = [at(0, 0), at(w - 1, 0), at(0, h - 1), at(w - 1, h - 1)];
  const transparent = corners.some((i) => data[i + 3] < 16);
  const bg = corners[0];
  const close = (i: number, j: number, tol: number) =>
    Math.abs(data[i] - data[j]) <= tol && Math.abs(data[i + 1] - data[j + 1]) <= tol && Math.abs(data[i + 2] - data[j + 2]) <= tol;
  if (!transparent && !corners.every((c) => close(c, bg, 18))) return null; // busy edges: a photo-like logo, leave it
  const isMark = (i: number) => (transparent ? data[i + 3] >= 16 : data[i + 3] >= 16 && !close(i, bg, 28));

  let minX = w, minY = h, maxX = -1, maxY = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (!isMark(at(x, y))) continue;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  if (maxX < 0) return null; // nothing drawn
  const bw = maxX - minX + 1, bh = maxY - minY + 1;
  if (bw >= w * 0.96 && bh >= h * 0.96) return null; // already tight
  const pad = Math.round(Math.max(bw, bh) * 0.06);
  const x = Math.max(0, minX - pad), y = Math.max(0, minY - pad);
  return { x, y, w: Math.min(w, maxX + 1 + pad) - x, h: Math.min(h, maxY + 1 + pad) - y };
}

/** A trimmed copy of the logo (or the same picture when there is nothing to cut). */
export function trimLogo<T extends CanvasImageSource & { width: number; height: number }>(pic: T): T | HTMLCanvasElement {
  // For an <img> on the page, width/height are its displayed size: use the picture's real size.
  const img = pic as unknown as { naturalWidth?: number; naturalHeight?: number };
  const w = img.naturalWidth || pic.width, h = img.naturalHeight || pic.height;
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  if (!ctx || w === 0 || h === 0) return pic;
  ctx.drawImage(pic, 0, 0, w, h);
  const box = contentBox(ctx.getImageData(0, 0, c.width, c.height).data, c.width, c.height);
  if (!box) return pic;
  const out = document.createElement('canvas');
  out.width = box.w;
  out.height = box.h;
  out.getContext('2d')?.drawImage(c, box.x, box.y, box.w, box.h, 0, 0, box.w, box.h);
  return out;
}
